import {GitPullRequestIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Spinner, Tooltip} from '@primer/react'
import {Suspense, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {ClosedByPullRequestsReferencesQuery} from './__generated__/ClosedByPullRequestsReferencesQuery.graphql'
import type {ClosedByPullRequestsReferencesListNode} from './closed-by-pull-requests-references-list-query-types'
import styles from './ClosedByPullRequestsReferences.module.css'
import {PullRequest} from './PullRequest'

// Fields read from the parent Issue node. Parent queries inline these fields directly.
// This type uses `| undefined` for Relay compatibility while parent components are still Relay-owned.
export type ClosedByPullRequestsReferencesFragment = {
  readonly closedByPullRequestsReferences:
    | {
        readonly totalCount: number
      }
    | null
    | undefined
}

type Props = {
  issueId: string
  closedByPullRequestsReferencesKey: ClosedByPullRequestsReferencesFragment
}

export function ClosedByPullRequestsReferences({issueId, closedByPullRequestsReferencesKey: data}: Props) {
  const totalCount = data?.closedByPullRequestsReferences?.totalCount ?? 0
  const [isClosedByPRRequested, setIsClosedByPRRequested] = useState(false)

  if (totalCount === 0) return null

  return totalCount === 1 ? (
    <Suspense
      fallback={
        <div>
          <GitPullRequestIcon size={16} />
          <span className={styles.pullRequestCountText}>{totalCount.toString()}</span>
        </div>
      }
    >
      {isClosedByPRRequested ? (
        <ClosedByPullRequestsReferencesInternal issueId={issueId} totalCount={totalCount} />
      ) : (
        <Button
          className={styles.linkedPullRequestsBtn}
          variant="link"
          leadingVisual={GitPullRequestIcon}
          onMouseEnter={() => {
            setIsClosedByPRRequested(true)
          }}
          onFocus={() => {
            setIsClosedByPRRequested(true)
          }}
          aria-label={'1 linked PR'}
        >
          {totalCount.toString()}
        </Button>
      )}
    </Suspense>
  ) : (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip direction="w" text={`${totalCount} linked PRs`} type="description">
          <Button
            variant="link"
            leadingVisual={GitPullRequestIcon}
            className={styles.linkedPullRequestsBtn}
            aria-label={`${totalCount} linked PRs`}
          >
            {totalCount.toString()}
          </Button>
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList className={styles.pullRequestsDropdown}>
          <Suspense fallback={<CenteredLoadingSpinner />}>
            <ClosedByPullRequestsReferencesInternal issueId={issueId} totalCount={totalCount} />
          </Suspense>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function ClosedByPullRequestsReferencesInternal({issueId, ...props}: {issueId: string; totalCount: number}) {
  const data = useLazyLoadQuery<ClosedByPullRequestsReferencesQuery>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      query ClosedByPullRequestsReferencesQuery($id: ID!) {
        node(id: $id) {
          ... on Issue {
            closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
              edges {
                node {
                  id
                  title
                  number
                  merged
                  closed
                  isDraft
                  repository {
                    name
                    owner {
                      login
                    }
                  }
                  url
                }
              }
            }
          }
        }
      }
    `,
    {
      id: issueId,
    },
  )

  if (!data.node) return <>{'no data'}</>

  // RELAY-CLEANUP(ClosedByPullRequestsReferencesInternal): remove cast when ClosedByPullRequestsReferencesQuery leaves Relay
  return (
    <ClosedByPullRequestsReferencesList
      closedByPullRequestsReferencesKey={data.node as ClosedByPullRequestsReferencesListFragment}
      {...props}
    />
  )
}

type ClosedByPullRequestsReferencesListFragment = Pick<
  ClosedByPullRequestsReferencesListNode,
  'closedByPullRequestsReferences'
>

function ClosedByPullRequestsReferencesList({
  closedByPullRequestsReferencesKey: data,
  totalCount,
}: {
  closedByPullRequestsReferencesKey: ClosedByPullRequestsReferencesListFragment
  totalCount: number
}) {
  const pullRequests = (data.closedByPullRequestsReferences?.edges || []).flatMap(a => (a && a.node ? a.node : []))

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
      {totalCount === 1 && <PullRequest key={`pr${0}`} pullRequest={pullRequests[0]!} hovercard />}
      {totalCount > 1 && (
        <>
          {pullRequests.map(pr => (
            <PullRequest key={`pr${pr.id}`} pullRequest={pr} />
          ))}
        </>
      )}
    </>
  )
}

function CenteredLoadingSpinner(): React.ReactElement {
  return (
    <div data-testid={'loading-pulls'} className={styles.loadingSpinnerContainer}>
      <Spinner />
    </div>
  )
}
